import { __decorate } from "tslib";
import { html, LitElement } from 'lit';
import { property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { autoUpdate, computePosition, flip, offset, shift, size } from '@floating-ui/dom';
import { setDefaultAnimation, getAnimation, startAnimations, stopAnimations } from '../../internal/animate.js';
import { emit, waitForEvent } from '../../internal/event.js';
import { watch } from '../../internal/watch.js';
import { getTabbableBoundary } from '../../internal/tabbable.js';
import { FLOATING_PLACEMENTS } from '../../internal/constants/placementConstants.js';
import { ARC_EVENTS } from '../../internal/constants/eventConstants.js';
import { ARC_ANIMATION_OPTIONS } from '../../internal/constants/animationConstants.js';
import styles from './arc-dropdown.styles.js';
export default class ArcDropdown extends LitElement {
    constructor() {
        super(...arguments);
        this.placement = FLOATING_PLACEMENTS['bottom-start'];
        this.distance = 0;
        this.skidding = 0;
        this.open = false;
        this.disabled = false;
        this.hoist = false;
    }
    async handleOpenChange() {
        if (this.disabled) {
            return;
        }
        this._updateAccessibleTrigger();
        if (this.open) {
            emit(this, ARC_EVENTS.show);
            this.addOpenListeners();
            await stopAnimations(this);
            this.startPositioner();
            this.panel.hidden = false;
            const { keyframes, options } = getAnimation(this, 'dropdown.show');
            await startAnimations(this.panel, keyframes, options);
            emit(this, ARC_EVENTS.afterShow);
        }
        else {
            emit(this, ARC_EVENTS.hide);
            this.removeOpenListeners();
            await stopAnimations(this);
            const { keyframes, options } = getAnimation(this, 'dropdown.hide');
            await startAnimations(this.panel, keyframes, options);
            this.panel.hidden = true;
            this.stopPositioner();
            emit(this, ARC_EVENTS.afterHide);
        }
    }
    handlePopoverOptionsChange() {
        this.updatePositioner();
    }
    handleDisabledChange() {
        if (this.disabled && this.open) {
            this.hide();
        }
    }
    connectedCallback() {
        super.connectedCallback();
        this.handlePanelSelect = this.handlePanelSelect.bind(this);
        this.handleDocumentKeyDown = this.handleDocumentKeyDown.bind(this);
        this.handleDocumentMouseDown = this.handleDocumentMouseDown.bind(this);
        if (!this.containingElement) {
            this.containingElement = this;
        }
    }
    async firstUpdated() {
        this.panel.hidden = !this.open;
        if (this.open) {
            await this.updateComplete;
            this.addOpenListeners();
            this.startPositioner();
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeOpenListeners();
        this.hide();
        this.stopPositioner();
    }
    startPositioner() {
        this.stopPositioner();
        this.updatePositioner();
        this._positionerCleanup = autoUpdate(this.trigger, this.positioner, this.updatePositioner.bind(this));
    }
    updatePositioner() {
        if (!this.open || !this.trigger || !this.positioner) {
            return;
        }
        computePosition(this.trigger, this.positioner, {
            placement: this.placement,
            middleware: [
                offset({ mainAxis: this.distance, crossAxis: this.skidding }),
                flip(),
                shift(),
                size({
                    apply: ({ width, height }) => {
                        Object.assign(this.panel.style, {
                            maxWidth: `${width}px`,
                            maxHeight: `${height}px`,
                        });
                    },
                    padding: 8,
                }),
            ],
            strategy: this.hoist ? 'fixed' : 'absolute',
        }).then(({ x, y, placement }) => {
            this.positioner.setAttribute('data-placement', placement);
            Object.assign(this.positioner.style, {
                position: this.hoist ? 'fixed' : 'absolute',
                left: `${x}px`,
                top: `${y}px`,
            });
        });
    }
    stopPositioner() {
        if (this._positionerCleanup) {
            this._positionerCleanup();
            this._positionerCleanup = undefined;
            this.positioner.removeAttribute('data-placement');
        }
    }
    _updateAccessibleTrigger() {
        if (this.trigger) {
            const assignedElements = this.triggerSlot.assignedElements({ flatten: true });
            const accessibleTrigger = assignedElements.find(el => getTabbableBoundary(el).start);
            if (accessibleTrigger) {
                accessibleTrigger.setAttribute('aria-haspopup', 'true');
                accessibleTrigger.setAttribute('aria-expanded', this.open ? 'true' : 'false');
            }
        }
    }
    addOpenListeners() {
        document.addEventListener('keydown', this.handleDocumentKeyDown);
        document.addEventListener('mousedown', this.handleDocumentMouseDown);
    }
    removeOpenListeners() {
        document.removeEventListener('keydown', this.handleDocumentKeyDown);
        document.removeEventListener('mousedown', this.handleDocumentMouseDown);
    }
    show() {
        if (this.open) {
            return undefined;
        }
        this.open = true;
        return waitForEvent(this, ARC_EVENTS.afterShow);
    }
    hide() {
        if (!this.open) {
            return undefined;
        }
        this.open = false;
        return waitForEvent(this, ARC_EVENTS.afterHide);
    }
    getMenu() {
        const slot = this.panel.querySelector('slot');
        return slot.assignedElements({ flatten: true }).filter(el => el.tagName === 'ARC-MENU')[0];
    }
    focusOnTrigger() {
        const trigger = this.triggerSlot.assignedElements({ flatten: true })[0];
        if (trigger && typeof trigger.focus === 'function') {
            trigger.focus();
        }
    }
    handleDocumentKeyDown(event) {
        var _a, _b, _c;
        if (event.key === 'Escape') {
            this.hide();
            this.focusOnTrigger();
            return;
        }
        if (event.key === 'Tab') {
            const activeElement = ((_a = this.containingElement) === null || _a === void 0 ? void 0 : _a.getRootNode()) instanceof ShadowRoot
                ? (_c = (_b = document.activeElement) === null || _b === void 0 ? void 0 : _b.shadowRoot) === null || _c === void 0 ? void 0 : _c.activeElement
                : document.activeElement;
            if (this.open && (activeElement === null || activeElement === void 0 ? void 0 : activeElement.tagName) === 'ARC-MENU-ITEM') {
                event.preventDefault();
                this.hide();
                this.focusOnTrigger();
            }
        }
    }
    handleDocumentMouseDown(event) {
        const path = event.composedPath();
        if (this.containingElement && !path.includes(this.containingElement)) {
            this.hide();
        }
    }
    handleTriggerKeyDown(event) {
        if (event.key === 'Escape') {
            this.focusOnTrigger();
            this.hide();
            return;
        }
        if ([' ', 'Enter'].includes(event.key)) {
            event.preventDefault();
            this.handleTriggerClick();
            return;
        }
        const menu = this.getMenu();
        const menuItems = menu ? [...menu.querySelectorAll('arc-menu-item')] : [];
        const firstMenuItem = menuItems[0];
        const lastMenuItem = menuItems[menuItems.length - 1];
        if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
            event.preventDefault();
            if (!this.open) {
                this.show();
            }
            if (event.key === 'ArrowDown' && firstMenuItem) {
                menu.setCurrentItem(firstMenuItem);
                firstMenuItem.focus();
                return;
            }
            if (event.key === 'ArrowUp' && lastMenuItem) {
                menu.setCurrentItem(lastMenuItem);
                lastMenuItem.focus();
                return;
            }
        }
        const ignoredKeys = ['Tab', 'Shift', 'Meta', 'Ctrl', 'Alt'];
        if (this.open && menu && !ignoredKeys.includes(event.key)) {
            menu.typeToSelect(event.key);
        }
    }
    handleTriggerKeyUp(event) {
        if (event.key === ' ') {
            event.preventDefault();
        }
    }
    handleTriggerClick() {
        if (this.open) {
            this.hide();
        }
        else {
            this.show();
        }
    }
    handlePanelSelect(event) {
        const target = event.target;
        if (target.tagName === 'ARC-MENU') {
            this.hide();
            this.focusOnTrigger();
        }
    }
    render() {
        return html `
      <div
        id="main"
        class=${classMap({
            dropdown: true,
            'dropdown--open': this.open,
        })}
      >
        <span
          id="trigger"
          @click=${this.handleTriggerClick}
          @keydown=${this.handleTriggerKeyDown}
          @keyup=${this.handleTriggerKeyUp}
        >
          <slot id="triggerSlot" name="trigger" @slotchange=${this._updateAccessibleTrigger}></slot>
        </span>

        <div id="positioner">
          <div
            id="panel"
            role="menu"
            aria-hidden=${this.open ? 'false' : 'true'}
            aria-labelledby="main"
            @arc-select=${this.handlePanelSelect}
          >
            <slot></slot>
          </div>
        </div>
      </div>
    `;
    }
}
ArcDropdown.tag = 'arc-dropdown';
ArcDropdown.styles = styles;
__decorate([
    query('#trigger')
], ArcDropdown.prototype, "trigger", void 0);
__decorate([
    query('#triggerSlot')
], ArcDropdown.prototype, "triggerSlot", void 0);
__decorate([
    query('#positioner')
], ArcDropdown.prototype, "positioner", void 0);
__decorate([
    query('#panel')
], ArcDropdown.prototype, "panel", void 0);
__decorate([
    property({ type: String })
], ArcDropdown.prototype, "placement", void 0);
__decorate([
    property({ attribute: false })
], ArcDropdown.prototype, "containingElement", void 0);
__decorate([
    property({ type: Number })
], ArcDropdown.prototype, "distance", void 0);
__decorate([
    property({ type: Number })
], ArcDropdown.prototype, "skidding", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], ArcDropdown.prototype, "open", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], ArcDropdown.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], ArcDropdown.prototype, "hoist", void 0);
__decorate([
    watch('open', { waitUntilFirstUpdate: true })
], ArcDropdown.prototype, "handleOpenChange", null);
__decorate([
    watch('distance'),
    watch('hoist'),
    watch('placement'),
    watch('skidding')
], ArcDropdown.prototype, "handlePopoverOptionsChange", null);
__decorate([
    watch('disabled')
], ArcDropdown.prototype, "handleDisabledChange", null);
setDefaultAnimation('dropdown.show', {
    keyframes: [
        { opacity: 0, transform: 'scale(0.9)' },
        { opacity: 1, transform: 'scale(1)' },
    ],
    options: ARC_ANIMATION_OPTIONS.fast,
});
setDefaultAnimation('dropdown.hide', {
    keyframes: [
        { opacity: 1, transform: 'scale(1)' },
        { opacity: 0, transform: 'scale(0.9)' },
    ],
    options: ARC_ANIMATION_OPTIONS.fast,
});
